/**
 * WorkshopPickerMonthNav — the booking `WorkshopPicker`'s month prev/next +
 * "Today" jump (BON-142). Rendered in the FIXED `PickerFilters` chrome row,
 * NOT inside the shared `WorkshopCalendarPicker`'s own `MonthHeader` (that
 * lives in the scrolling grid and scrolls out of view) — so the nav stays
 * reachable while browsing, with a one-click way back to the current month.
 */
import { addMonths, format, subMonths } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface WorkshopPickerMonthNavProps {
  month: Date;
  onMonthChange: (month: Date) => void;
}

export function WorkshopPickerMonthNav({ month, onMonthChange }: WorkshopPickerMonthNavProps) {
  return (
    <div role="group" aria-label="Month" className="flex flex-shrink-0 items-center gap-1">
      <button
        type="button"
        aria-label="Previous month"
        onClick={() => onMonthChange(subMonths(month, 1))}
        className="rounded-md p-1.5 text-stone-500 transition hover:bg-stone-100 hover:text-stone-700 dark:hover:bg-[#252b26] dark:hover:text-stone-200"
      >
        <ChevronLeft size={16} />
      </button>
      <span className="min-w-[120px] text-center text-sm font-bold uppercase tracking-wide text-bark-900 dark:text-stone-100">
        {format(month, 'MMMM yyyy')}
      </span>
      <button
        type="button"
        aria-label="Next month"
        onClick={() => onMonthChange(addMonths(month, 1))}
        className="rounded-md p-1.5 text-stone-500 transition hover:bg-stone-100 hover:text-stone-700 dark:hover:bg-[#252b26] dark:hover:text-stone-200"
      >
        <ChevronRight size={16} />
      </button>
      <button
        type="button"
        onClick={() => onMonthChange(new Date())}
        aria-label="Back to current month"
        className="ml-1 rounded-lg border border-stone-200 px-2.5 py-1 text-xs font-semibold text-stone-600 transition hover:border-stone-300 dark:border-stone-700 dark:text-stone-300"
      >
        Today
      </button>
    </div>
  );
}
